import { useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { MenuSelect, type MenuSelectOption } from "./MenuSelect";

import { colors, spacing, typography } from "@/lib/theme";

interface DatePickerFieldProps {
    value: string | undefined;
    onChange: (value: string) => void;
    readOnly?: boolean;
    /** Oldest selectable year. Defaults to 100 years before the current year. */
    minYear?: number;
    /** Newest selectable year. Defaults to the current year. */
    maxYear?: number;
}

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

const MONTH_OPTIONS: MenuSelectOption[] = MONTHS.map((label, i) => ({
    label,
    value: String(i + 1).padStart(2, "0")
}));

function pad(n: number): string {
    return String(n).padStart(2, "0");
}

function daysInMonth(month: number, year: number): number {
    // month is 1-12. Day 0 of next month = last day of this month. Falls back
    // to 31 when month/year unknown so every day stays selectable.
    if (!month) {
        return 31;
    }
    return new Date(year || 2000, month, 0).getDate();
}

interface DateParts {
    month: string;
    day: string;
    year: string;
}

/** Parse a stored "MM/DD/YYYY" answer into zero-padded parts (or blanks). */
function parse(value: string | undefined): DateParts {
    const parts = (value ?? "").split("/");
    const m = Number(parts[0]);
    const d = Number(parts[1]);
    return {
        month: m >= 1 && m <= 12 ? pad(m) : "",
        day: d >= 1 && d <= 31 ? pad(d) : "",
        year: /^\d{4}$/.test(parts[2] ?? "") ? parts[2] : ""
    };
}

/**
 * Month / Day / Year date picker built from three dropdowns. Stores the answer
 * as "MM/DD/YYYY". Replaces the old free-text DateInput. Defaults to a date-of-
 * birth-friendly year range (last 100 years) and clamps the day to the selected
 * month/year so Feb 30 can't be entered.
 */
export function DatePickerField({
    value,
    onChange,
    readOnly,
    minYear,
    maxYear
}: DatePickerFieldProps) {
    const initial = parse(value);
    const [month, setMonth] = useState(initial.month);
    const [day, setDay] = useState(initial.day);
    const [year, setYear] = useState(initial.year);

    // Re-seed local state when a complete date arrives from outside (e.g. a
    // WebSocket-driven refetch of the saved answer).
    useEffect(() => {
        const p = parse(value);
        if (p.month && p.day && p.year) {
            setMonth(p.month);
            setDay(p.day);
            setYear(p.year);
        }
    }, [value]);

    const yearOptions = useMemo<MenuSelectOption[]>(() => {
        const now = new Date().getFullYear();
        const hi = maxYear ?? now;
        const lo = minYear ?? now - 100;
        const opts: MenuSelectOption[] = [];
        for (let y = hi; y >= lo; y--) {
            opts.push({ label: String(y), value: String(y) });
        }
        return opts;
    }, [minYear, maxYear]);

    const dayOptions = useMemo<MenuSelectOption[]>(() => {
        const max = daysInMonth(Number(month), Number(year));
        const opts: MenuSelectOption[] = [];
        for (let d = 1; d <= max; d++) {
            opts.push({ label: String(d), value: pad(d) });
        }
        return opts;
    }, [month, year]);

    function commit(next: DateParts) {
        if (next.month && next.day && next.year) {
            onChange(`${next.month}/${next.day}/${next.year}`);
        }
    }

    function handleMonth(m: string) {
        // Clamp the day if the new month is shorter (e.g. switching to February).
        const maxDay = daysInMonth(Number(m), Number(year));
        const nextDay = day && Number(day) > maxDay ? pad(maxDay) : day;
        setMonth(m);
        setDay(nextDay);
        commit({ month: m, day: nextDay, year });
    }

    function handleDay(d: string) {
        setDay(d);
        commit({ month, day: d, year });
    }

    function handleYear(y: string) {
        // Re-clamp the day for Feb 29 in a non-leap year.
        const maxDay = daysInMonth(Number(month), Number(y));
        const nextDay = day && Number(day) > maxDay ? pad(maxDay) : day;
        setYear(y);
        setDay(nextDay);
        commit({ month, day: nextDay, year: y });
    }

    if (readOnly) {
        const display = month && day && year ? `${month}/${day}/${year}` : "—";
        return (
            <View style={styles.readOnly}>
                <Text style={styles.readOnlyText}>{display}</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View style={styles.field}>
                <Text style={styles.fieldLabel}>Month</Text>
                <MenuSelect
                    value={month}
                    options={MONTH_OPTIONS}
                    onChange={handleMonth}
                    placeholder="Select month"
                />
            </View>
            <View style={styles.row}>
                <View style={styles.half}>
                    <Text style={styles.fieldLabel}>Day</Text>
                    <MenuSelect
                        value={day}
                        options={dayOptions}
                        onChange={handleDay}
                        placeholder="Day"
                    />
                </View>
                <View style={styles.half}>
                    <Text style={styles.fieldLabel}>Year</Text>
                    <MenuSelect
                        value={year}
                        options={yearOptions}
                        onChange={handleYear}
                        placeholder="Year"
                    />
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: spacing.sm,
        marginTop: spacing.xs
    },
    field: {
        gap: 4
    },
    row: {
        flexDirection: "row",
        gap: spacing.sm
    },
    half: {
        flex: 1,
        gap: 4
    },
    fieldLabel: {
        ...typography.caption,
        color: colors.textTertiary,
        marginLeft: 2
    },
    readOnly: {
        marginTop: spacing.xs
    },
    readOnlyText: {
        ...typography.body,
        color: colors.textSecondary
    }
});
